import { useState, useEffect, useRef } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import rawDataService from './services/rawDataService'
// 다운로드 패키징 전역 감시 — 새로고침/화면 이동과 무관하게 완료 알림·자동 다운로드 유지
import './services/packagingWatcher'
import Login from './pages/Login'
import Layout from './components/Layout'
import Dashboard from './pages/Dashboard'
import RawDataManagement from './pages/RawDataManagement'
import LabelDataManagement from './pages/LabelDataManagement'
import DatasetManagement from './pages/DatasetManagement'
import TrainHistoryManagement from './pages/TrainHistoryManagement'
import ModelReleaseManagement from './pages/ModelReleaseManagement'
import TestDatasetManagement from './pages/TestDatasetManagement'
import RawCollectionReport from './pages/RawCollectionReport'
import ImageExplorer from './pages/ImageExplorer'
import { PERMISSIONS } from './constants/permissions'
import UserManagement from './pages/UserManagement'
import NoPermission from './pages/NoPermission'
import config from './config'
import GlobalUploadBanner from './components/GlobalUploadBanner'
import { hasFeature, hasPermission } from './utils/permissions'

// 사용자 정보 타입
type UserInfo = {
  email: string
  fullName: string
  isAdmin: boolean
  permissions: string[]
  // 이 배치에서 켜진 기능 (/auth/me 의 features). 없으면(옛 백엔드) 전부 켜진 것으로 본다.
  features?: string[]
  // 이 배치의 리전 코드 (/auth/me 의 region). kr·us 면 Sidebar 가 리전 색·실루엣을 입힌다 (폐쇄망 kr-local 은 기본 모습).
  region?: string
}

// /auth/me 응답을 UserInfo 로. 로그인·자동 로그인·토큰 갱신 세 경로가 전부 이걸 쓴다 —
// 한 경로라도 features 를 빠뜨리면 "목록 없음 = 전부 켜짐"이 되어 edge 배치에서
// 숨겨야 할 메뉴가 다 보인다 (2026-09-11 북미 첫 로그인에서 실제로 그랬다).
// 페이지 키 → 기능 이름 (Sidebar 의 menuItems 와 같은 대응). 첫 화면을 고르거나 주소로 바로
// 들어올 때도 메뉴와 같은 기준으로 막아야 한다 — 권한만 보면 edge 배치에서 숨긴 화면이
// 주소로는 열린다.
const PAGE_FEATURE: Record<string, string> = {
  'dashboard': 'dashboard', 'raw-data': 'raw_data', 'image-explorer': 'image_explorer',
  'label-data': 'label_data', 'dataset': 'dataset', 'test-dataset': 'test_dataset',
  'training': 'training', 'deployment': 'deployment', 'users': 'users', 'report': 'report',
}
function canOpen(user: UserInfo, pageKey: string, perm: string): boolean {
  return hasFeature(user, PAGE_FEATURE[pageKey]) && hasPermission(user, perm)
}

function userInfoFromMe(userData: any): UserInfo {
  const perms: string[] = Array.isArray(userData?.permissions) ? userData.permissions : []
  return {
    email: userData?.email,
    fullName: userData?.full_name,
    isAdmin: perms.includes('admin'),
    permissions: perms,
    features: Array.isArray(userData?.features) ? userData.features : undefined,
    region: typeof userData?.region === 'string' ? userData.region : undefined,
  }
}

function AppContent() {
  const location = useLocation()
  const navigate = useNavigate()
  const pageRoutes: Record<string, string> = {
    'dashboard': '/manager/dashboard',
    'raw-data': '/manager/raw-data',
    'image-explorer': '/manager/image-explorer',
    'label-data': '/manager/label-data',
    'dataset': '/manager/dataset',
    'test-dataset': '/manager/test-dataset',
    'training': '/manager/training',
    'deployment': '/manager/deployment',
    'users': '/manager/users',
    'report': '/report',
  }
  const matchPageFromPath = (path: string) => {
    const match = Object.entries(pageRoutes).find(([, route]) => path.startsWith(route))
    if (!match) return null
    return { key: match[0], route: match[1] }
  }
  const getPageFromPath = (path: string) => {
    const m = matchPageFromPath(path)
    return m ? m.key : 'dashboard'
  }
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [currentPage, setCurrentPage] = useState('dashboard')
  const [userInfo, setUserInfo] = useState<UserInfo>({
    email: '',
    fullName: '',
    isAdmin: false,
    permissions: [],
  })
  const [bootstrapped, setBootstrapped] = useState(false)
  const handling401Ref = useRef(false)
  const handling403Ref = useRef(false)
  const refreshInFlightRef = useRef<Promise<boolean> | null>(null)

  // 컴포넌트 마운트 시 자동 로그인 체크
  useEffect(() => {
    const checkAutoLogin = async () => {
      // 부팅 시 로컬 미완료 세션 정리 (TTL/손상)
      try { rawDataService.pruneLocalSessions() } catch {}
      // 남아있을 수 있는 전역 취소 플래그 제거
      try { localStorage.removeItem('upload_abort_v1') } catch {}
      const accessToken = localStorage.getItem('access_token')
      if (!accessToken) { setBootstrapped(true); return }

      try {
        const response = await fetch(`${config.API_BASE_URL}/auth/me`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        })

        if (response.ok) {
          const userData = await response.json()
          const nextUser: UserInfo = userInfoFromMe(userData)
          setUserInfo(nextUser)
          setIsLoggedIn(true)

          const orderedPages: { key: string; perm: string }[] = [
            { key: 'dashboard', perm: PERMISSIONS.PAGE_DASHBOARD },
            { key: 'raw-data', perm: PERMISSIONS.PAGE_RAW_DATA },
            { key: 'image-explorer', perm: PERMISSIONS.PAGE_IMAGE_EXPLORER },
            { key: 'label-data', perm: PERMISSIONS.PAGE_LABEL_DATA },
            { key: 'dataset', perm: PERMISSIONS.PAGE_DATASET },
            { key: 'test-dataset', perm: PERMISSIONS.PAGE_DATASET },
            { key: 'training', perm: PERMISSIONS.PAGE_TRAINING },
            { key: 'deployment', perm: PERMISSIONS.PAGE_DEPLOYMENT },
            { key: 'users', perm: PERMISSIONS.PAGE_USER_MANAGEMENT },
            { key: 'report', perm: PERMISSIONS.PAGE_RAW_COLLECTION_REPORT },
          ]
          const permByKey: Record<string, string> = Object.fromEntries(orderedPages.map(p => [p.key, p.perm]))

          // 새로고침/직접 URL 접근 시: 현재 경로가 /manager/* 이고 권한이 있으면 그대로 유지
          const requestedPath = window.location.pathname
          if (requestedPath === '/report') {
            if (canOpen(nextUser, 'report', PERMISSIONS.PAGE_RAW_COLLECTION_REPORT)) {
              setCurrentPage('report')
              setBootstrapped(true)
              return
            }
            navigate('/no-permission', { replace: true })
            setCurrentPage('dashboard')
            setBootstrapped(true)
            return
          }
          const requested = matchPageFromPath(requestedPath)
          if (requested && requestedPath.startsWith('/manager/')) {
            const perm = permByKey[requested.key]
            if (perm && canOpen(nextUser, requested.key, perm)) {
              setCurrentPage(requested.key)
              setBootstrapped(true)
              return
            }
          }

          const firstAllowed = orderedPages.find(p =>
            canOpen(nextUser, p.key, p.perm)
          )
          if (firstAllowed) {
            navigate(pageRoutes[firstAllowed.key], { replace: true })
            setCurrentPage(firstAllowed.key)
          } else {
            navigate('/no-permission', { replace: true })
            setCurrentPage('dashboard')
          }
        } else {
          // access 토큰이 만료/무효이면 refresh 시도 후 재요청
          try {
            const rt = localStorage.getItem('refresh_token')
            if (rt) {
              const r = await fetch(`${config.API_BASE_URL}/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh_token: rt })
              })
              if (r.ok) {
                const data = await r.json()
                if (data?.access_token) localStorage.setItem('access_token', data.access_token)
                if (data?.refresh_token) localStorage.setItem('refresh_token', data.refresh_token)
                const me2 = await fetch(`${config.API_BASE_URL}/auth/me`, {
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                    'Content-Type': 'application/json',
                  },
                })
                if (me2.ok) {
                  const userData = await me2.json()
                  setUserInfo(userInfoFromMe(userData))
                  setIsLoggedIn(true)
                  return
                }
              }
            }
          } catch {}

          // 리프레시 실패 시 세션 만료 플래그 설정 후 토큰 정리
          try { localStorage.setItem('session_expired', '1') } catch {}
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
        }
      } catch (error) {
        console.error('Auto login error:', error)
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
      }
      setBootstrapped(true)
    }

    checkAutoLogin()
  }, [])

  // 전역 401 처리 + 리프레시 토큰 자동 갱신 후 재시도
  useEffect(() => {
    const originalFetch = window.fetch.bind(window)

    const forceLogout = () => {
      try { localStorage.setItem('session_expired', '1') } catch {}
      setIsLoggedIn(false)
      setCurrentPage('dashboard')
      setUserInfo({ email: '', fullName: '', isAdmin: false, permissions: [] })
      try { localStorage.removeItem('access_token') } catch {}
      try { localStorage.removeItem('refresh_token') } catch {}
    }

    const refreshAccessToken = async (): Promise<boolean> => {
      // 동시 갱신 방지
      if (refreshInFlightRef.current) return refreshInFlightRef.current
      const p = (async () => {
        const rt = localStorage.getItem('refresh_token')
        if (!rt) return false
        try {
          const resp = await originalFetch(`${config.API_BASE_URL}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Skip-Auth-Refresh': '1' },
            body: JSON.stringify({ refresh_token: rt }),
          })
          if (!resp.ok) return false
          const data = await resp.json()
          if (data?.access_token) localStorage.setItem('access_token', data.access_token)
          if (data?.refresh_token) localStorage.setItem('refresh_token', data.refresh_token)
          return true
        } catch {
          return false
        }
      })()
      refreshInFlightRef.current = p
      const ok = await p
      refreshInFlightRef.current = null
      return ok
    }

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const response = await originalFetch(input as any, init as any)
      if (!response) return response
      const urlStr = typeof input === 'string' || input instanceof URL ? String(input) : (input as Request).url

      if (response.status === 401) {
        // 재시도 이미 수행 여부 판단
        const originalHeaders = new Headers((init && (init as RequestInit).headers) || {})
        const alreadyRetried = originalHeaders.get('X-Auth-Retry') === '1'

        // 인증 관련 엔드포인트는 리프레시 시도하지 않음
        const isAuthRefresh = urlStr.includes('/auth/refresh')
        const isAuthLogin = urlStr.includes('/auth/login')
        if (isAuthRefresh || isAuthLogin) return response

        if (alreadyRetried) {
          if (!handling401Ref.current) {
            handling401Ref.current = true
            try { alert('세션이 만료되었습니다. 다시 로그인해주세요.') } catch {}
            forceLogout()
            setTimeout(() => { handling401Ref.current = false }, 800)
          }
          return response
        }

        // 리프레시 시도
        const ok = await refreshAccessToken()
        if (!ok) {
          if (!handling401Ref.current) {
            handling401Ref.current = true
            try { alert('세션이 만료되었습니다. 다시 로그인해주세요.') } catch {}
            forceLogout()
            setTimeout(() => { handling401Ref.current = false }, 800)
          }
          return response
        }

        // 새 토큰으로 원 요청 1회 재시도
        try {
          const retryInit: RequestInit = { ...(init || {}) }
          const headers = new Headers(retryInit.headers || {})
          const access = localStorage.getItem('access_token')
          if (access) headers.set('Authorization', `Bearer ${access}`)
          headers.set('X-Auth-Retry', '1')
          retryInit.headers = headers
          const retryResp = await originalFetch(input as any, retryInit)
          if (retryResp.status === 401) {
            if (!handling401Ref.current) {
              handling401Ref.current = true
              try { alert('세션이 만료되었습니다. 다시 로그인해주세요.') } catch {}
              forceLogout()
              setTimeout(() => { handling401Ref.current = false }, 800)
            }
          }
          return retryResp
        } catch {
          return response
        }
      }

      if (response.status === 403) {
        if (!handling403Ref.current) {
          handling403Ref.current = true
          try { alert('접근 권한이 없습니다. 관리자에게 권한을 요청하세요.') } catch {}
          try { setCurrentPage('dashboard') } catch {}
          setTimeout(() => { handling403Ref.current = false }, 800)
        }
        return response
      }

      return response
    }

    return () => {
      window.fetch = originalFetch
    }
  }, [])

  const handleLoginSuccess = async (_username: string) => {
    try {
      // 저장된 토큰으로 사용자 정보 가져오기
      const accessToken = localStorage.getItem('access_token')
      if (!accessToken) {
        console.error('No access token found')
        return
      }

      const response = await fetch(`${config.API_BASE_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        const userData = await response.json()
        const nextUser: UserInfo = userInfoFromMe(userData)
        setUserInfo(nextUser)
        setIsLoggedIn(true)

        // 로그인 직후: 사용자가 보고 있던(요청한) /manager/* 경로가 유효하고 권한이 있으면 유지
        const orderedPages: { key: string; perm: string }[] = [
          { key: 'dashboard', perm: PERMISSIONS.PAGE_DASHBOARD },
          { key: 'raw-data', perm: PERMISSIONS.PAGE_RAW_DATA },
          { key: 'image-explorer', perm: PERMISSIONS.PAGE_IMAGE_EXPLORER },
          { key: 'label-data', perm: PERMISSIONS.PAGE_LABEL_DATA },
          { key: 'dataset', perm: PERMISSIONS.PAGE_DATASET },
          { key: 'training', perm: PERMISSIONS.PAGE_TRAINING },
          { key: 'deployment', perm: PERMISSIONS.PAGE_DEPLOYMENT },
          { key: 'users', perm: PERMISSIONS.PAGE_USER_MANAGEMENT },
          { key: 'report', perm: PERMISSIONS.PAGE_RAW_COLLECTION_REPORT },
        ]
        const permByKey: Record<string, string> = Object.fromEntries(orderedPages.map(p => [p.key, p.perm]))
        const requestedPath = window.location.pathname
        if (requestedPath === '/report') {
          if (canOpen(nextUser, 'report', PERMISSIONS.PAGE_RAW_COLLECTION_REPORT)) {
            setCurrentPage('report')
            return
          }
          navigate('/no-permission', { replace: true })
          setCurrentPage('dashboard')
          return
        }
        const requested = matchPageFromPath(requestedPath)
        if (requested && requestedPath.startsWith('/manager/')) {
          const perm = permByKey[requested.key]
          if (perm && canOpen(nextUser, requested.key, perm)) {
            setCurrentPage(requested.key)
            return
          }
        }

        const firstAllowed = orderedPages.find(p =>
          canOpen(nextUser, p.key, p.perm)
        )

        if (firstAllowed) {
          navigate(pageRoutes[firstAllowed.key], { replace: true })
          setCurrentPage(firstAllowed.key)
        } else {
          navigate('/no-permission', { replace: true })
        setCurrentPage('dashboard')
        }
      } else {
        console.error('Failed to fetch user info:', response.status)
        // 토큰이 유효하지 않으면 삭제
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
      }
    } catch (error) {
      console.error('Error fetching user info:', error)
      // 에러 발생 시 토큰 삭제
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
    }
  }

  const handleLogout = () => {
    setIsLoggedIn(false)
    setCurrentPage('dashboard')
    setUserInfo({ email: '', fullName: '', isAdmin: false, permissions: [] })
    // 이미지 서빙 쿠키는 서버가 지운다 (HttpOnly 라 여기서 못 지운다).
    // 안 지우면 로그아웃해도 만료까지 이미지가 계속 열린다. 응답을 기다리지
    // 않는 이유는 화면 전환을 막지 않기 위해서다 — 실패해도 토큰은 이미
    // 지웠으므로 다른 API 는 통하지 않는다.
    fetch(`${config.API_BASE_URL}/auth/logout`, { method: 'POST' }).catch(() => {})
    try { localStorage.removeItem('session_expired') } catch {}
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    navigate('/login', { replace: true })
  }

  // 페이지 변경 핸들러
  const handlePageChange = (page: string) => {
    const target = pageRoutes[page] || pageRoutes['dashboard']
    if (location.pathname !== target) {
      navigate(target)
    }
    setCurrentPage(page)
  }

  useEffect(() => {
    setCurrentPage(getPageFromPath(location.pathname))
  }, [location.pathname])

  // 로그인 화면
  if (!isLoggedIn) {
    if (!bootstrapped) {
      return (
        <div className="w-screen h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center text-gray-700">
            <div className="text-lg font-semibold">로딩 중...</div>
            <div className="text-sm text-gray-500 mt-1">서버 응답을 기다리고 있습니다.</div>
          </div>
        </div>
      )
    }
    return <Login onLoginSuccess={handleLoginSuccess} />
  }

  // 로그인 후 메인 화면
  return (
    <Layout 
      currentPage={currentPage}
      onLogout={handleLogout}
      userInfo={userInfo}
      onPageChange={handlePageChange}
    >
      <Routes>
        <Route
          path="/manager/dashboard"
          element={
            hasPermission(userInfo, PERMISSIONS.PAGE_DASHBOARD)
              ? <Dashboard userInfo={userInfo} />
              : (
                <Navigate
                  to={
                    (() => {
                      const orderedPages: { key: string; perm: string }[] = [
                        { key: 'raw-data', perm: PERMISSIONS.PAGE_RAW_DATA },
                        { key: 'image-explorer', perm: PERMISSIONS.PAGE_IMAGE_EXPLORER },
                        { key: 'label-data', perm: PERMISSIONS.PAGE_LABEL_DATA },
                        { key: 'dataset', perm: PERMISSIONS.PAGE_DATASET },
                        { key: 'test-dataset', perm: PERMISSIONS.PAGE_DATASET },
                        { key: 'training', perm: PERMISSIONS.PAGE_TRAINING },
                        { key: 'deployment', perm: PERMISSIONS.PAGE_DEPLOYMENT },
                        { key: 'users', perm: PERMISSIONS.PAGE_USER_MANAGEMENT },
                        { key: 'report', perm: PERMISSIONS.PAGE_RAW_COLLECTION_REPORT },
                      ]
                      const found = orderedPages.find(p =>
                        hasPermission(userInfo, p.perm)
                      )
                      return found ? pageRoutes[found.key] : '/no-permission'
                    })()
                  }
                  replace
                />
              )
          }
        />
        <Route
          path="/manager/raw-data"
          element={
            hasPermission(userInfo, PERMISSIONS.PAGE_RAW_DATA)
              ? <RawDataManagement userInfo={userInfo} />
              : <Navigate to="/manager/dashboard" replace />
          }
        />
        <Route
          path="/manager/image-explorer"
          element={
            hasPermission(userInfo, PERMISSIONS.PAGE_IMAGE_EXPLORER)
              ? <ImageExplorer userInfo={userInfo} />
              : <Navigate to="/manager/dashboard" replace />
          }
        />
        <Route
          path="/manager/label-data"
          element={
            hasPermission(userInfo, PERMISSIONS.PAGE_LABEL_DATA)
              ? <LabelDataManagement userInfo={userInfo} />
              : <Navigate to="/manager/dashboard" replace />
          }
        />
        <Route
          path="/manager/dataset"
          element={
            hasPermission(userInfo, PERMISSIONS.PAGE_DATASET)
              ? <DatasetManagement userInfo={userInfo} />
              : <Navigate to="/manager/dashboard" replace />
          }
        />
        <Route
          path="/manager/test-dataset"
          element={
            hasPermission(userInfo, PERMISSIONS.PAGE_DATASET)
              ? <TestDatasetManagement userInfo={userInfo} />
              : <Navigate to="/manager/dashboard" replace />
          }
        />
        <Route
          path="/manager/training"
          element={
            hasPermission(userInfo, PERMISSIONS.PAGE_TRAINING)
              ? <TrainHistoryManagement userInfo={userInfo} />
              : <Navigate to="/manager/dashboard" replace />
          }
        />
        <Route
          path="/manager/deployment"
          element={
            hasPermission(userInfo, PERMISSIONS.PAGE_DEPLOYMENT)
              ? <ModelReleaseManagement userInfo={userInfo} />
              : <Navigate to="/manager/dashboard" replace />
          }
        />
        <Route path="/manager/users" element={userInfo.isAdmin ? <UserManagement userInfo={userInfo} /> : <Navigate to="/manager/dashboard" replace />} />
        <Route path="/report" element={hasPermission(userInfo, PERMISSIONS.PAGE_RAW_COLLECTION_REPORT) ? <RawCollectionReport userInfo={userInfo} /> : <Navigate to="/no-permission" replace />} />
        <Route path="/manager" element={<Navigate to="/manager/dashboard" replace />} />
        <Route path="/no-permission" element={<NoPermission />} />
        <Route path="*" element={<Navigate to="/manager/dashboard" replace />} />
      </Routes>
      <GlobalUploadBanner />
    </Layout>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  )
}

// 브라우저 종료/새로고침 시 세션은 유지(이어받기 목적). 다만 전역 ABORT 플래그만 내려 루프 중단.
window.addEventListener('beforeunload', () => {
  try { localStorage.setItem('upload_abort_v1', '1') } catch {}
})
